<script lang="ts">
  import { navigate } from 'svelte-routing';

  import { isLoggedIn } from './utils/login.service.js';
  import {
    RoutingService,
    Route,
    Column,
    CreateRoute,
    SortDirection
  } from './utils/routing.service.js';

  import ActionBar from './components/ActionBar.svelte';
  import CreateEditModal from './components/modals/routing/CreateEditModal.svelte';
  import { addToast } from './components/toast/store.js';
  import RoutingTable from './components/RoutingTable.svelte';
  import DeleteModal from './components/modals/DeleteModal.svelte';
  import { SelectOption } from './components/form/SelectWithLabel.svelte';
  import { getUserErrorMessage } from './utils/error.js';
  import Main from './components/Main.svelte';

  if (!$isLoggedIn) {
    navigate('/login');
  }

  const routingService = new RoutingService();
  const pageSize = 50;
  const columns: Column[] = [
    {
      label: 'URL-Pfad',
      sortable: true,
      field: 'url_path',
      formatter: rawFieldContent =>
        `<a href="https://www.adac.de${rawFieldContent}" target="_blank">${rawFieldContent}</a>`
    },
    {
      label: 'Target',
      sortable: true,
      field: 'target'
    },
    {
      label: 'Verzeichnis-Flag',
      sortable: true,
      field: 'wildcard'
    },
    {
      label: 'Veröffentlicht',
      sortable: false,
      field: 'published'
    }
  ];
  const targetOptions: SelectOption[] = [
    { label: '3.0', value: '3.0' },
    { label: '4.0', value: '4.0' },
    { label: '3.0 & 4.0', value: '3.0 & 4.0' }
  ];

  let routings: Route[] = [];
  let totalCount: number = 0;
  let sortBy: string | undefined = 'url_path';
  let sortDirection: SortDirection = 'DESC';
  let currentPageIndex: number = 0;
  let selectedTarget: string;

  let deleteModalOpen: boolean;
  let createOrEditModalOpen: boolean;

  let routeToDelete: Route;
  let routeToEdit: Route | undefined;

  const toggleCreateOrEditModalOpen = () => {
    createOrEditModalOpen = !createOrEditModalOpen;
  };

  const updateRouteList = async () => {
    const { data, totalCount: responseTotalCount } = await routingService.getList({
      limit: pageSize,
      sortBy,
      sortDirection,
      offset: currentPageIndex * pageSize,
      ...(selectedTarget === '3.0' || selectedTarget === '4.0' ? { target: selectedTarget } : {})
    });
    routings = data;
    totalCount = responseTotalCount;
  };

  const createRoute = async (route: CreateRoute) => {
    if (!route.url_path) {
      addToast({
        type: 'error',
        message: 'Bitte alle Felder ausfüllen.'
      });
      return;
    }

    try {
      await routingService.createRoute(route);
      await updateRouteList();
      addToast({
        type: 'success',
        message: 'Route wurde erfolgreich hinzugefügt.'
      });
      toggleCreateOrEditModalOpen();
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unerwarteter Fehler beim Hinzufügen der Route.')
      });
    }
  };

  const editRoute = async (route: Route) => {
    if (!route.url_path) {
      addToast({
        type: 'error',
        message: 'Bitte alle Felder ausfüllen.'
      });
      return;
    }

    try {
      await routingService.updateRoute(route);
      await updateRouteList();
      addToast({
        type: 'success',
        message: 'Route wurde erfolgreich geändert.'
      });
      toggleCreateOrEditModalOpen();
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unerwarteter Fehler beim Ändern der Route.')
      });
    }
  };

  const deleteRoute = async (route: Route) => {
    try {
      await routingService.delete(route.id);
      await updateRouteList();
      addToast({
        type: 'success',
        message: 'Route wurde erfolgreich gelöscht.'
      });
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unerwarteter Fehler beim Löschen der Route.')
      });
    }
  };

  const onSelectedTargetChange = async (event: Event) => {
    currentPageIndex = 0;
    selectedTarget = (event.target as HTMLSelectElement)?.value ?? '';
    await updateRouteList();
  };

  const publishRoutings = async () => {
    try {
      await routingService.publish();
      await updateRouteList();
      addToast({
        type: 'success',
        message: 'Das Veröffentlichen der Routings wurde erfolgreich angestoßen.'
      });
    } catch (e) {
      addToast({
        type: 'error',
        message: 'Das Veröffentlichen der Routings konnte nicht angestoßen werden.'
      });
    }
  };

  $: sortBy, sortDirection, currentPageIndex, updateRouteList();
</script>

<Main>
  <ActionBar
    createNewEntryLabel="Routing-Anweisung hinzufügen"
    {toggleCreateOrEditModalOpen}
    publishEntries={publishRoutings}
    selectLabel="Target: "
    selectPlaceholder="Wählen Sie eine Option"
    selectOptions={targetOptions}
    selectedOption={selectedTarget}
    onSelectOptionChange={onSelectedTargetChange}
  />

  <RoutingTable
    {routings}
    {columns}
    {totalCount}
    {pageSize}
    bind:sortBy
    bind:sortDirection
    bind:currentPageIndex
    onDeleteClick={routing => {
      routeToDelete = routing;
      deleteModalOpen = true;
    }}
    onEditClick={routing => {
      routeToEdit = routing;
      createOrEditModalOpen = true;
    }}
  />

  <section data-role="modals">
    <CreateEditModal
      bind:isOpen={createOrEditModalOpen}
      route={routeToEdit}
      onCreateRoute={createRoute}
      onUpdateRoute={editRoute}
    />
    <DeleteModal bind:isOpen={deleteModalOpen} onDelete={() => deleteRoute(routeToDelete)} />
  </section>
</Main>

<style lang="scss">
  @use './styles/spacing';

  :global(body) {
    min-height: 100vh;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    font-family: MiloADAC, sans-serif;
  }
</style>
